import React from 'react'

import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { EnvironmentIndicator } from '@/shared/components/atoms/EnvironmentIndicator'
import { Separator } from '@/shared/components/atoms/Separator'
import AuthenticatedLayout from '@/shared/components/organisms/Layout/AuthenticatedLayout/AuthenticatedLayout'
import { cn } from '@/shared/utils/helpers'

interface TabItem {
	key: string
	label: string
}

interface BackButtonConfig {
	show: boolean
	onClick?: () => void
	to?: string
}

interface PageLayoutProps {
	/**
	 * Main title for the page
	 */
	title?: string
	/**
	 * Subtitle or description text
	 */
	subtitle?: string
	/**
	 * Actions to display in the header (e.g., buttons)
	 */
	headerActions?: React.ReactNode
	/**
	 * Actions to display next to the title (back button layout)
	 */
	titleActions?: React.ReactNode
	/**
	 * Tab items for tabbed navigation
	 */
	tabs?: TabItem[]
	/**
	 * Currently active tab key
	 */
	activeTab?: string
	/**
	 * Handler for tab changes
	 */
	onTabChange?: (tabKey: string) => void
	/**
	 * Main content of the page
	 */
	children: React.ReactNode
	/**
	 * Whether to show separator after header (default: true)
	 */
	showSeparator?: boolean
	/**
	 * Additional classes for content section
	 */
	contentClassName?: string
	/**
	 * Additional classes for container
	 */
	containerClassName?: string
	/**
	 * Back button configuration
	 */
	backButton?: BackButtonConfig
	/**
	 * Custom header content (replaces title/subtitle/tabs)
	 */
	customHeader?: React.ReactNode
	/**
	 * Optional content shown in the top app bar (center area)
	 */
	topAppBarContent?: React.ReactNode
	/**
	 * Optional content shown in the top app bar (left area)
	 */
	topAppBarLeftContent?: React.ReactNode
	/**
	 * Whether to require authentication (default: true)
	 */
	requireAuth?: boolean
	/**
	 * Whether to show the top app bar (default: true)
	 */
	showTopAppBar?: boolean
}

/**
 * PageLayout - Reusable layout component for internal pages
 *
 * This component provides a consistent layout structure across the application.
 * Based on the SchedulesListPageUI pattern with support for tabs, headers, and back buttons.
 *
 * @example
 * // Simple page with title and subtitle
 * <PageLayout title="Schedules" subtitle="Manage your schedules and playlists">
 *   <div>Content here</div>
 * </PageLayout>
 *
 * @example
 * // Page with tabs
 * <PageLayout
 *   tabs={[{ key: 'zones', label: 'Zones' }, { key: 'groups', label: 'Groups' }]}
 *   activeTab={activeTab}
 *   onTabChange={handleTabChange}
 * >
 *   <div>Content here</div>
 * </PageLayout>
 *
 * @example
 * // Page with back button
 * <PageLayout
 *   title="Group details"
 *   backButton={{ show: true, to: '/zones?tab=group' }}
 * >
 *   <div>Content here</div>
 * </PageLayout>
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
	title,
	subtitle,
	headerActions,
	titleActions,
	tabs,
	activeTab,
	onTabChange,
	children,
	showSeparator = true,
	contentClassName = '',
	containerClassName = 'md:border rounded-xl overflow-hidden md:bg-card',
	backButton,
	customHeader,
	topAppBarContent,
	topAppBarLeftContent,
	requireAuth = true,
	showTopAppBar = true,
}) => {
	const navigate = useNavigate()
	const location = useLocation()
	const isZonesView = location.pathname === '/zones'

	const handleBackClick = () => {
		if (backButton?.onClick) {
			backButton.onClick()
		} else if (backButton?.to) {
			navigate(backButton.to)
		}
	}

	// Determine if we should show any header
	const hasHeader = customHeader || title || subtitle || tabs || backButton

	return (
		<AuthenticatedLayout
			requireAuth={requireAuth}
			showTopAppBar={showTopAppBar}
			topAppBarContent={topAppBarContent}
			topAppBarLeftContent={topAppBarLeftContent}
		>
			{/* Show environment indicator */}
			<EnvironmentIndicator />

			<div className="w-full md:px-3 2xl:pl-6 2xl:pr-6 md:py-3 2xl:py-6">
				<div className="max-w-[1400px] mx-auto w-full">
					<div className={containerClassName}>
						{/* Header Section */}
						{hasHeader && (
							<>
								<div
									className={cn(
										isZonesView && 'py-3',
										'md:px-4 md:py-6 lg:px-8 md:border-border'
									)}
								>
									{customHeader ? (
										customHeader
									) : backButton?.show ? (
										// Back button layout (GroupDetails style)
										<div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-0 px-3 py-5 md:px-0 md:py-0">
											<div className="flex items-center justify-between w-full md:w-auto gap-4">
												<div className="flex items-center gap-4">
													<button
														onClick={handleBackClick}
														className="text-foreground hover:text-muted-foreground transition-colors"
													>
														<ArrowLeft className="h-6 w-6 md:h-8 md:w-8" />
													</button>
													<h1 className="md:text-3xl text-xl font-normal text-foreground">
														{title}
													</h1>
												</div>
												{titleActions && <div>{titleActions}</div>}
											</div>
											{headerActions && (
												<div className="w-full md:w-auto flex justify-end md:justify-start shrink-0">
													{headerActions}
												</div>
											)}
										</div>
									) : tabs ? (
										// Tabs layout (Zones style)
										<div className="flex justify-between md:justify-start items-center md:items-start space-x-3 md:space-x-6">
											{tabs.map((tab) => (
												<button
													id={`page-layout-tab-${tab.key}`}
													data-testid={`page-layout-tab-${tab.key}`}
													key={tab.key}
													onClick={() => onTabChange?.(tab.key)}
													className={`flex-1 md:flex-none text-sm  md:text-3xl md:font-normal rounded-full px-6 py-2 md:px-0 md:py-0 tracking-tight transition-colors ${
														activeTab === tab.key
															? 'text-primary-foreground bg-outline md:bg-transparent'
															: 'text-muted-foreground hover:text-foreground/80'
													}`}
												>
													{tab.label}
												</button>
											))}
										</div>
									) : (
										// Title/subtitle layout (Schedules style)
										<div className="flex items-center justify-between px-3 py-5 md:px-0 md:py-0">
											<div className="space-y-0">
												{title && (
													<h2 className="flex items-center text-2xl font-normal tracking-tight md:text-3xl">
														{title}
													</h2>
												)}
												{subtitle && (
													<p className="text-xs md:text-sm font-medium text-muted-foreground">
														{subtitle}
													</p>
												)}
											</div>
											{headerActions && <div>{headerActions}</div>}
										</div>
									)}
								</div>

								{/* Separator - only show on desktop */}
								{showSeparator && <Separator className="hidden md:block" />}
							</>
						)}

						{/* Main Content Section */}
						<div
							className={cn(
								'px-4 lg:px-8 py-4 lg:py-6 md:bg-card',
								contentClassName
							)}
						>
							{children}
						</div>
					</div>
				</div>
			</div>
		</AuthenticatedLayout>
	)
}

export default PageLayout
